import { useState } from 'react'
import { toast } from 'react-toastify'
import { returnPayoutCheck } from 'services'
import { Checkbox, Input2, Modal } from 'stories/components'
import { formatDate, thousandSeperatorWithPrefix } from 'utils'

/** The little a payment has to tell this dialog about itself. */
export interface ReturnablePayout {
  id: number
  payeeName?: string
  amount?: number | string
  referenceNumber?: string
  paymentMethod?: string
}

/**
 * Today where the user is, not in UTC. `toISOString().slice(0, 10)` is the habit elsewhere in this
 * repo and it is wrong after 5pm Pacific: it offers tomorrow, and a return dated a day late is
 * booked on the wrong day in the trust ledger by someone who never looked at the field.
 */
const today = () => formatDate(new Date(), 'yyyy-MM-dd')

/**
 * Record that the bank sent a payout back.
 *
 * Deliberately NOT the same action as Reprint, which sits next to it in two of the three places
 * this is offered. Reprint says the check never got cashed, so the payment is undone and its
 * disbursement removed. This says the payment went out and the money came back, so the payment
 * stands and a deposit books the return: that is what makes the trust ledger agree with a bank
 * statement showing the debit and then the credit. The copy says so out loud, because choosing the
 * wrong one of the two leaves books that cannot be reconciled and nothing on screen to show why.
 *
 * Shared by the loan's Party Payments screen, a payee's payment history and the Check Queue's
 * remittance detail, so the same wording and the same choice appear wherever a returned payment is
 * noticed. Asked for by Longhorn, Smartsheet row 379.
 */
export const ReturnPayoutModal = ({
  payout,
  onClose,
  onDone,
}: {
  payout: ReturnablePayout
  onClose: () => void
  onDone: () => void
}) => {
  const [returnedDate, setReturnedDate] = useState(today())
  const [reason, setReason] = useState('')
  const [reissue, setReissue] = useState(true)
  const [saving, setSaving] = useState(false)
  const amount = Number(payout.amount) || 0

  const onSubmit = async () => {
    setSaving(true)
    const res: any = await returnPayoutCheck({ payableId: payout.id, returnedDate, reason, reissue }).catch(() => null)
    setSaving(false)
    if (!res?.success) {
      toast(res?.message || 'Could not record this return.', { type: 'error' })
      return false
    }
    // A return that posted no deposit is the one outcome the servicer has to act on themselves, so
    // it is left on screen rather than passed in a toast that disappears.
    toast(res.message, { type: res.ledgerPosted ? 'success' : 'warning', autoClose: res.ledgerPosted ? 5000 : false })
    onDone()
    return true
  }

  return (
    <Modal
      title="Returned by the Bank"
      isOpen
      onClose={onClose}
      onOk={onSubmit}
      titleOkay="Record Return"
      titleCancel="Cancel"
      loading={saving}
      disabled={saving}
    >
      <div className="w-[30rem] max-w-full">
        <p className="text-sm text-gray-600 mb-3">
          Record that <span className="font-semibold">{thousandSeperatorWithPrefix(amount)}</span> paid to{' '}
          <span className="font-semibold">{payout.payeeName || 'this payee'}</span>
          {!!payout.referenceNumber && (
            <>
              {' '}
              under <span className="font-semibold">{payout.referenceNumber}</span>
            </>
          )}{' '}
          came back.
        </p>
        <p className="text-xs text-gray-500 mb-4">
          The payment stays on the file with its reference and its paid date, and a deposit for the same amount books
          the money coming back, so the trust ledger shows the round trip the way the bank statement does. Use Reprint
          instead if the check was never cashed.
        </p>

        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <Input2
              title="Date Returned"
              type="date"
              value={returnedDate}
              onChange={(v: string) => setReturnedDate(v)}
            />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <Input2
              title="Reason"
              type="text"
              value={reason}
              placeholder="Account closed, stale dated, R03…"
              onChange={(v: string) => setReason(v)}
            />
          </div>
        </div>

        <div className="mt-3">
          <Checkbox
            id="reissue-returned-payout"
            title="Put it back in the check queue to pay again"
            value={reissue}
            onChange={(value: boolean) => setReissue(value)}
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Leave this off to record the return and decide later. Nothing is paid now either way: the copy waits in the
            queue until a payout run picks it up.
          </p>
        </div>
      </div>
    </Modal>
  )
}
